import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { ReactNode } from 'react';

import { getMarketTradingCalendarApi } from '@flipster/apis/v2/market';
import { GetMarketTradingCalendarResponse, MarketStatus, TradingCalendarEntry } from '@flipster/apis/v2/market/types';
import { InstrumentApi } from '@flipster/apis/v2/snapshot/types';
import { T } from '@flipster/i18n';
import { overlayBunja } from '@flipster/overlay';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { timestampToDate } from '@flipster/utils/datetime';

import { isTradFiInstrument } from '../rwa';
import { snapshotSpecBunja } from '../snapshot/state';
import { SymbolContext, SymbolScope } from '../symbol/bunja';
import {
  findCurrentEntryIndex,
  getOffHoursAlertDontShowAgain,
  isTradableOffHours,
  type OffHoursAction,
} from './off-hours';
import { confirmOffHoursAlert } from './off-hours-alert';
import { TradingHoursNoticeModal } from './trading-hours-notice-modal';
import { formatSessionWindow, getWeeklySchedule, sessionLabels, type DailySchedule } from './utils';

export type SessionWindow = {
  status: string;
  start: Date;
  end: Date | null;
  label: string;
  formattedWindow: ReactNode;
};

export const tradingSessionCheckBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const overlay = bunja.use(overlayBunja);
  const symbol = bunja.use(SymbolScope);
  const { isTradableAtom, isTradableOffHoursAtom } = bunja.use(tradingSessionBunja);

  const openTradingHoursNoticeModal = (options?: { showTradabilityStatus?: boolean; onConfirm?: () => void }) => {
    overlay.open(({ isOpen, close, unmount }) => (
      <SymbolContext value={symbol}>
        <TradingHoursNoticeModal
          isOpen={isOpen}
          onClose={() => {
            options?.onConfirm?.();
            close();
          }}
          onExit={unmount}
          showTradabilityStatus={options?.showTradabilityStatus}
        />
      </SymbolContext>
    ));
  };

  const checkTradable = (options?: { onClosePreviousModal?: () => void }): boolean => {
    if (store.get(isTradableAtom) === false) {
      options?.onClosePreviousModal?.();
      openTradingHoursNoticeModal();
      return false;
    }
    return true;
  };

  /**
   * Tradable off-hours 중 "alert 필요" 액션(주문/포지션 관리) 실행 직전에 호출한다.
   * 현재 상태(off-hours 여부 / 다시 보지 않기)를 모아 confirmOffHoursAlert로 위임.
   * @returns 액션을 진행해도 되면 true, 사용자가 다이얼로그를 닫아 취소했으면 false.
   */
  const guardOffHoursAlert = (action: OffHoursAction): Promise<boolean> =>
    confirmOffHoursAlert({
      isTradableOffHours: store.get(isTradableOffHoursAtom),
      dontShowAgain: getOffHoursAlertDontShowAgain(),
      action,
    });

  return { checkTradable, guardOffHoursAlert, openTradingHoursNoticeModal };
});
tradingSessionCheckBunja.debugLabel = 'tradingSessionCheckBunja';

export const tradingSessionBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const symbol = bunja.use(SymbolScope);
  const { instrumentsKeyBySymbolAtom } = bunja.use(snapshotSpecBunja);

  const marketStatusRefreshAtom = atom(0);
  const symbolInfoAtom = atom((get) => get(instrumentsKeyBySymbolAtom)?.[symbol]);

  const tradefiInformationQueryAtom = atomWithQuery((get) => {
    const symbolInfo = get(symbolInfoAtom);
    const isTradFi = isTradFiInstrument(symbolInfo);
    return {
      queryKey: ['getMarketTradingCalendarApi', symbol],
      queryFn: () => getMarketTradingCalendarApi(),
      select: (response: Awaited<ReturnType<typeof getMarketTradingCalendarApi>>) => response.data,
      enabled: isTradFi,
      staleTime: 60 * 60 * 1000,
      gcTime: 24 * 60 * 60 * 1000,
    };
  });

  const tradefiCalendarEntriesAtom = atom<TradingCalendarEntry[] | null>((get) => {
    const calendar = get(tradefiInformationQueryAtom).data;
    const symbolInfo = get(symbolInfoAtom);
    const calendarKey = symbolInfo?.tradingCalendar;
    if (!calendar || !calendarKey) return null;
    return calendar[calendarKey] ?? null;
  });

  const currentMarketStatusAtom = atom<MarketStatus | null>((get) => {
    get(marketStatusRefreshAtom);
    const entries = get(tradefiCalendarEntriesAtom);
    if (!entries) return null;
    return getCurrentMarketStatus(entries);
  });

  bunja.effect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const scheduleRefresh = () => {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
      const entries = store.get(tradefiCalendarEntriesAtom);
      if (!entries) return;
      const nextChange = getNextStatusChangeTime(entries);
      if (nextChange === null) return;
      const delay = Math.max(nextChange - Date.now(), 0);
      timeoutId = setTimeout(() => {
        store.set(marketStatusRefreshAtom, (c) => c + 1);
        scheduleRefresh();
      }, delay);
    };
    const unsub = store.sub(tradefiCalendarEntriesAtom, scheduleRefresh);
    scheduleRefresh();
    return () => {
      unsub();
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  });

  const tradableSessionsAtom = atom<string[]>((get) => {
    const symbolInfo = get(symbolInfoAtom);
    return symbolInfo?.tradableMarketSessions ?? [];
  });

  const isTradableAtom = atom<boolean>((get) => {
    get(marketStatusRefreshAtom);
    const symbolInfo = get(symbolInfoAtom);
    const calendarData = get(tradefiInformationQueryAtom).data;
    return isSymbolTradable(symbolInfo, calendarData);
  });

  /**
   * 현재 Tradable off-hours 상태인지 여부 (Off Hours Alert / 경고 노티스 대상).
   * marketStatusRefreshAtom을 구독해 세션 경계 시각에 자동 갱신된다.
   */
  const isTradableOffHoursAtom = atom<boolean>((get) => {
    get(marketStatusRefreshAtom);
    const symbolInfo = get(symbolInfoAtom);
    const calendarData = get(tradefiInformationQueryAtom).data;
    return isTradableOffHours(symbolInfo, calendarData, Date.now());
  });

  /** 현재 심볼이 TradFi 상품인지 여부 (off-hours 배너 노출 게이팅). */
  const isTradFiInstrumentAtom = atom<boolean>((get) => isTradFiInstrument(get(symbolInfoAtom)));

  /**
   * tradableMarketSessions에 속하는 각 세션의 다음 윈도우를 반환합니다.
   * 인터벌 규칙: [start, end) — 경계값 충돌 방지
   */
  const upcomingSessionWindowsAtom = atom<SessionWindow[]>((get) => {
    get(marketStatusRefreshAtom);
    const entries = get(tradefiCalendarEntriesAtom);
    const tradableSessions = get(tradableSessionsAtom);
    if (!entries || tradableSessions.length === 0) return [];

    const currentIdx = findCurrentEntryIndex(entries, Date.now());
    const futureStart = currentIdx + 1;

    return tradableSessions.flatMap((status) => {
      const window =
        findCurrentWindow(entries, currentIdx, futureStart, status) ?? findNextWindow(entries, futureStart, status);
      return window ? [window] : [];
    });
  });

  const weeklyTradingScheduleAtom = atom<DailySchedule[]>((get) => {
    get(marketStatusRefreshAtom);
    const entries = get(tradefiCalendarEntriesAtom);
    const tradableSessions = get(tradableSessionsAtom);
    if (!entries || tradableSessions.length === 0) return [];
    return getWeeklySchedule(entries, tradableSessions, Date.now());
  });

  const isDateOutsideTradingHours = (date: Date): boolean => {
    const symbolInfo = store.get(symbolInfoAtom);
    const entries = store.get(tradefiCalendarEntriesAtom);
    const tradableSessions = store.get(tradableSessionsAtom);
    const calendarKey = symbolInfo?.tradingCalendar;
    if (calendarKey === 'TRADING_CALENDAR_247' || !entries || entries.length === 0) return false;
    const idx = findCurrentEntryIndex(entries, date.getTime());
    // Ignore last entry's status https://prextech.slack.com/archives/C09HZUHP8QH/p1774846446308429
    if (idx < 0 || !entries[idx + 1]) return false;
    return !tradableSessions.includes(entries[idx].status);
  };

  return {
    tradefiInformationQueryAtom,
    tradefiCalendarEntriesAtom,
    tradableSessionsAtom,
    currentMarketStatusAtom,
    isTradableAtom,
    isTradableOffHoursAtom,
    isTradFiInstrumentAtom,
    upcomingSessionWindowsAtom,
    weeklyTradingScheduleAtom,
    isDateOutsideTradingHours,
  };
});
tradingSessionBunja.debugLabel = 'tradingSessionBunja';

export const isSymbolTradable = (
  symbolInfo: InstrumentApi | undefined | null,
  calendarData: GetMarketTradingCalendarResponse | undefined | null,
): boolean => {
  if (!isTradFiInstrument(symbolInfo)) return true;
  const calendarKey = symbolInfo?.tradingCalendar;
  if (!calendarData || !calendarKey || calendarKey === 'TRADING_CALENDAR_247') return true;
  const entries = calendarData[calendarKey];
  if (!entries) return true;
  const tradableSessions = symbolInfo?.tradableMarketSessions ?? [];
  const currentStatus = getCurrentMarketStatus(entries);
  return currentStatus != null && tradableSessions.includes(currentStatus);
};

export const getCurrentMarketStatus = (entries: TradingCalendarEntry[]): MarketStatus | null => {
  if (!entries.length) return null;
  const currentIdx = findCurrentEntryIndex(entries, Date.now());
  return currentIdx === -1 ? null : entries[currentIdx].status;
};

const getNextStatusChangeTime = (entries: TradingCalendarEntry[]): number | null => {
  if (!entries.length) return null;
  const now = Date.now();
  const currentIdx = findCurrentEntryIndex(entries, now);
  const nextEntry =
    currentIdx !== -1 ? entries[currentIdx + 1] : entries.find((e) => timestampToDate(e.timestamp).getTime() > now);
  return nextEntry ? timestampToDate(nextEntry.timestamp).getTime() : null;
};

const formatSessionEntry = (status: string, start: Date, end: Date | null): SessionWindow => {
  const label = sessionLabels[status] ?? status;
  const formattedWindow =
    status === 'HOLIDAY' ? <T id='Trade.r260318.Tradfi_trading_hours_holiday' /> : formatSessionWindow(start, end);
  return { status, start, end, label, formattedWindow };
};

const findCurrentWindow = (
  entries: TradingCalendarEntry[],
  currentIdx: number,
  futureStart: number,
  status: string,
): SessionWindow | null => {
  if (currentIdx < 0 || entries[currentIdx].status !== status) return null;
  const start = timestampToDate(entries[currentIdx].timestamp);
  const end = entries[futureStart] ? timestampToDate(entries[futureStart].timestamp) : null;
  return formatSessionEntry(status, start, end);
};

const findNextWindow = (entries: TradingCalendarEntry[], futureStart: number, status: string): SessionWindow | null => {
  const foundIdx = entries.findIndex((e, i) => i >= futureStart && e.status === status);
  if (foundIdx === -1) return null;
  const start = timestampToDate(entries[foundIdx].timestamp);
  const end = entries[foundIdx + 1] ? timestampToDate(entries[foundIdx + 1].timestamp) : null;
  return formatSessionEntry(status, start, end);
};
